/* RESPONSIBLE TEAM: team-standalone */
import Service, { inject as service } from '@ember/service';
import { taskFor } from 'ember-concurrency-ts';
import { dropTask, restartableTask } from 'ember-concurrency-decorators';
import { tracked } from '@glimmer/tracking';
import RSVP from 'rsvp';
import type ZendeskConfiguration from 'embercom/models/standalone/zendesk-configuration';
import type FreshdeskConfiguration from 'embercom/models/standalone/freshdesk-configuration';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type SalesforceLiveChatConfiguration from 'embercom/models/standalone/salesforce-live-chat-configuration';
import type SalesforceInAppMessagingConfiguration from 'embercom/models/standalone/salesforce-in-app-messaging-configuration';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import { get, put } from 'embercom/lib/ajax';
import type ContentImportService from './content-import-service';
import { type TaskGenerator } from 'ember-concurrency';
import type RouterService from '@ember/routing/router-service';
import type IntercomMessengerConfiguration from 'embercom/models/standalone/intercom-messenger-configuration';
import type OfficeHoursSchedule from 'embercom/models/office-hours-schedule';
import type KnowledgeHubService from './knowledge-hub-service';
import {
  calculateHighestIntegrationState,
  IntegrationState,
  RESPONSE_PROVIDED_ERRORS,
  StandalonePlatform,
  StandalonePlatformToIcon,
} from 'embercom/objects/standalone/constants';
import {
  ZendeskDataFields,
  type ZendeskTicketsSetupData,
} from 'embercom/objects/standalone/data-fields/zendesk';
import {
  type SalesforceSetupData,
  type SalesforceImportOnlyConfig,
} from 'embercom/objects/standalone/salesforce';
import { SalesforceDataFields } from 'embercom/objects/standalone/data-fields/salesforce';
import type { TicketField, UserField } from 'embercom/objects/standalone/data-fields/common';
import ZendeskDataLoader from 'embercom/services/fin-standalone-service/zendesk-data-loader';
import SalesforceDataLoader from 'embercom/services/fin-standalone-service/salesforce-data-loader';
import { type KnowledgeSourceSummary } from 'embercom/objects/standalone/knowledge';
import { type SunshineChannel, type ZendeskBrand } from 'embercom/objects/standalone/zendesk';
import { type FreshdeskTicketsSetupData } from 'embercom/objects/standalone/freshdesk';
import type ApplicationInstance from '@ember/application/instance';
import type { InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type Integration from 'embercom/models/standalone/integration';
import { IntegrationType } from 'embercom/objects/standalone/constants';
import type ExternalFieldIdentifier from 'embercom/models/standalone/external-field-identifier';
import type EmberArray from '@ember/array';

export default class FinStandaloneService extends Service {
  @service declare store: Store;
  @service declare appService: { app: $TSFixMe };
  @service declare notificationsService: $TSFixMe;
  @service declare permissionsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare contentImportService: ContentImportService;
  @service declare router: RouterService;
  @service declare knowledgeHubService: KnowledgeHubService;

  @tracked intercomMessengerConfig!: IntercomMessengerConfiguration;
  @tracked messengerSettings!: $TSFixMe;
  @tracked operatorIdentity?: $TSFixMe;
  @tracked operatorIdentityPreviewImgSrc = '';
  @tracked operatorIdentityPreviewDarkImgSrc = '';

  /* Salesforce specific */
  salesforceDataLoader: SalesforceDataLoader;
  @tracked salesforceConfig?: SalesforceConfiguration;
  @tracked salesforceImportOnlyConfig?: SalesforceImportOnlyConfig;
  @tracked salesforceLiveChatConfig?: SalesforceLiveChatConfiguration;
  @tracked salesforceInAppMessagingConfig?: SalesforceInAppMessagingConfiguration;
  @tracked salesforceSetupData: SalesforceSetupData = {
    admins: [],
    queues: [],
    caseOrigins: [],
    supportEmails: [],
    hasPermissionSet: undefined,
    hasPermissionSetAssignedToInstaller: undefined,
  };
  @tracked selectedSalesforceCaseOrigins: Array<string> = [];
  @tracked salesforceDataFields: SalesforceDataFields = new SalesforceDataFields({
    userFields: [],
    caseFields: [],
    cvdasForRequiredSalesforceCaseFields: [],
  });

  @tracked hasRequiredSalesforceFinPermissions = false;
  @tracked hasPermissionPackageBeenInstalled = false;

  /* Zendesk specific */
  zendeskDataLoader: ZendeskDataLoader;
  @tracked zendeskConfig?: ZendeskConfiguration;
  @tracked zendeskBrands: ZendeskBrand[] = [];
  @tracked sunshineChannels: SunshineChannel[] = [];

  /* Freshdesk specific */
  @tracked freshdeskConfig?: FreshdeskConfiguration;
  @tracked freshdeskTicketsSetupData: FreshdeskTicketsSetupData = {
    fin_user_id: undefined,
    all_possible_fin_users: [],
  };
  @tracked zendeskTicketsSetupData: ZendeskTicketsSetupData = {
    admins: [],
    email_addresses: [],
    live_trigger: { id: undefined },
  };
  @tracked zendeskDataFields: ZendeskDataFields = new ZendeskDataFields({
    userFields: [],
    ticketFields: [],
    organizationFields: [],
  });
  @tracked zendeskSchedules: Array<OfficeHoursSchedule> = [];
  @tracked isConversationSyncingEnabled: boolean | undefined = undefined;
  @tracked standaloneIntegrations: Integration[] = [];

  constructor(owner: ApplicationInstance) {
    super(owner);
    this.zendeskDataLoader = new ZendeskDataLoader(this);
    this.salesforceDataLoader = new SalesforceDataLoader(this);
  }

  get app() {
    return this.appService.app;
  }

  get workflowsReturnRoute(): string {
    let current = this.router.currentRouteName;

    if (current === 'apps.app.standalone.channels.zendesk.tickets.workflow') {
      return 'apps.app.standalone.deploy.zendesk.tickets.workflow.show';
    }

    if (current === 'apps.app.standalone.channels.zendesk.messaging.workflow') {
      return 'apps.app.standalone.deploy.zendesk.messaging.workflow.show';
    }

    if (current === 'apps.app.standalone.deploy.salesforce.cases.workflow') {
      return 'apps.app.standalone.deploy.salesforce.cases';
    }

    if (current === 'apps.app.standalone.channels.intercom.messenger.workflow') {
      return 'apps.app.standalone.deploy.intercom-messenger.integrate';
    }

    if (current === 'apps.app.standalone.tasks.workflow') {
      return 'apps.app.standalone.tasks';
    }

    console.error('No return route defined for: ', current);

    return 'apps.app.standalone.setup';
  }

  get showAnalyzeSectionConnectCta() {
    let platform = this.app.standalone_platforms.firstObject.platform_type as StandalonePlatform;

    return (
      [IntegrationState.NotConfigured, IntegrationState.Created].includes(
        this.connectionStateForPlatform(platform),
      ) && !this.isHistoricalSyncingEnabledForPlatform(platform)
    );
  }

  // Returns the "highest" state for a platform, e.g. if Zendesk enabled for tickets but not for sunshine, this will return IntegrationState.Enabled
  // We use this to know whether or not we should show some of the Analyze features or not
  connectionStateForPlatform(platform: StandalonePlatform): IntegrationState {
    switch (platform) {
      case StandalonePlatform.Zendesk:
        return calculateHighestIntegrationState([
          this.zendeskConfig?.highestIntegrationState,
          this.intercomMessengerConfig.integrationState,
        ]);
      case StandalonePlatform.Salesforce:
        return calculateHighestIntegrationState([
          this.salesforceConfig?.status,
          this.intercomMessengerConfig.integrationState,
        ]);
      case StandalonePlatform.Freshdesk:
        return calculateHighestIntegrationState([
          this.freshdeskConfig?.ticketState,
          this.intercomMessengerConfig.integrationState,
        ]);
      default: {
        console.error(
          `connectionStateForPlatform: Connection state logic not defined for platform:`,
          platform,
        );
        return IntegrationState.NotConfigured;
      }
    }
  }

  isHistoricalSyncingEnabledForPlatform(platform: StandalonePlatform) {
    switch (platform) {
      case StandalonePlatform.Zendesk:
        return this.isConversationSyncingEnabled;
      case StandalonePlatform.Salesforce:
        return this.isConversationSyncingEnabled;
      default: {
        console.error(
          `isHistoricalSyncingEnabledForPlatform: Historical syncing logic not defined for platform:`,
          platform,
        );
        return false;
      }
    }
  }

  get hasContentForFin() {
    return Object.keys(this.knowledgeSourceSummary).some((key) => {
      return this.knowledgeSourceSummary[Number(key)].used_by_fin_count > 0;
    });
  }

  get initialized() {
    return (
      taskFor(this.initializationTask)?.isRunning ||
      taskFor(this.initializationTask)?.performCount > 0
    );
  }

  async initialize() {
    if (!this.app.canUseStandalone) {
      return;
    }

    let initializationTaskInstance = taskFor(this.initializationTask).last;

    if (!initializationTaskInstance) {
      await taskFor(this.initializationTask).perform();
    } else if (initializationTaskInstance.isRunning) {
      await initializationTaskInstance;
    }
  }

  @dropTask *initializationTask() {
    let promises: Promise<unknown>[] = [
      taskFor(this.loadIntercomMessengerConfig).perform(),
      this.store.findAll('conversation-attributes/descriptor'),
      this.store.findAll('workflow-connector/insertable-action'),
      taskFor(this.peekOrLoadMessengerSettings).perform(),
    ];

    if (this.app.canUseFeature('team-standalone-fin-over-api')) {
      promises.push(this.store.findRecord('standalone/api-configuration', this.app.id));
    }

    if (this.app.hasStandalonePlatform('zendesk')) {
      promises.push(this.zendeskDataLoader.performLoads());
    }

    if (this.app.hasStandalonePlatform('salesforce')) {
      promises.push(this.salesforceDataLoader.performLoads());
    }

    promises.push(this.fetchStandaloneImportSource());

    if (this.app.hasStandalonePlatform('freshdesk')) {
      promises.push(taskFor(this.loadFreshdeskConfig).perform());
    }

    promises.push(taskFor(this.loadStandaloneIntegrations).perform());

    this.contentImportService.fetchContentImportSources();
    this.knowledgeHubService.fetchKnowledgeUsageSummary();

    try {
      yield RSVP.all(promises);

      // We need salesforce config loaded to efficiently load the setup data
      if (this.app.hasStandalonePlatform('salesforce')) {
        this.salesforceDataLoader.performLazyLoads();
      }

      // We need freshdesk config loaded to efficiently load the setup data
      if (this.app.hasStandalonePlatform('freshdesk')) {
        yield taskFor(this.loadFreshdeskTicketsSetupData).perform();
      }
    } catch (error) {
      console.error(error);
    }

    // this call is here to populate the contentLibrarySummary for Fin step in workflows
    taskFor(this.loadAiContentLibrarySummary).perform();
    taskFor(this.loadOperatorIdentity).perform();
  }

  async loadBasePlatformConfigs() {
    if (!this.app.canUseStandalone) {
      return;
    }

    if (this.initialized) {
      return;
    }

    if (this.app.hasStandalonePlatform('zendesk')) {
      taskFor(this.zendeskDataLoader.loadZendeskConfig).perform();
    }

    if (this.app.hasStandalonePlatform('salesforce')) {
      taskFor(this.salesforceDataLoader.loadSalesforceConfig).perform();
    }
  }

  @dropTask *loadFreshdeskConfig() {
    try {
      this.freshdeskConfig = yield this.store.findRecord(
        'standalone/freshdesk-configuration',
        this.app.id,
      );

      if (!this.freshdeskConfig) {
        this.freshdeskConfig = this.store.createRecord('standalone/freshdesk-configuration', {
          id: this.app.id,
        });
      }
    } catch (e) {
      console.error(e.message || e.jqXHR);
      if (e.jqXHR?.status !== 404) {
        throw e;
      }
    }
  }

  @dropTask *loadFreshdeskTicketsSetupData(): TaskGenerator<void> {
    if (this.freshdeskConfig?.isPendingSetupForTickets) {
      this.freshdeskTicketsSetupData = {
        fin_user_id: undefined,
        all_possible_fin_users: [],
      };

      return;
    }

    let freshdeskIntegrationId = this.standaloneIntegrations.find(
      (integration) => integration.integrationType === IntegrationType.FreshdeskTickets,
    )?.id;

    if (!freshdeskIntegrationId) {
      return;
    }

    try {
      let setupData = yield get(
        `/ember/standalone/integrations/${IntegrationType.FreshdeskTickets}/${freshdeskIntegrationId}/fin_user_identity`,
        {
          app_id: this.app.id,
        },
      );

      this.freshdeskTicketsSetupData = {
        fin_user_id: setupData.fin_user_id,
        all_possible_fin_users: setupData.all_possible_fin_users || [],
      };
    } catch (e) {
      console.error(e.message || e.jqXHR);
      // Set empty data on error
      this.freshdeskTicketsSetupData = {
        fin_user_id: undefined,
        all_possible_fin_users: [],
      };
    }
  }

  @dropTask *loadIntercomMessengerConfig(): TaskGenerator<void> {
    try {
      this.intercomMessengerConfig = yield this.store.findRecord(
        'standalone/intercom-messenger-configuration',
        this.app.id,
      );
    } catch (e) {
      console.error(e.message || e.jqXHR);
      if (e.jqXHR?.status !== 404) {
        throw e;
      }
    }
  }

  @dropTask *loadStandaloneIntegrations(): TaskGenerator<void> {
    let integrations = yield this.store.findAll('standalone/integration');
    this.standaloneIntegrations = integrations;
  }

  get isLoadingSunshineChannels() {
    return this.zendeskDataLoader?.isLoadingSunshineChannels || false;
  }

  get isZendeskConnectedWithSyncingEnabled() {
    return this.isZendeskConnected && this.isConversationSyncingEnabled;
  }

  get isZendeskConnected() {
    return this.zendeskConfig?.isConnected;
  }

  get isSalesforceConnectedWithSyncingEnabled() {
    return this.isSalesforceConnected && this.isConversationSyncingEnabled;
  }

  get isSalesforceConnected() {
    return this.salesforceConfig?.isConnected;
  }

  get knowledgeSourceSummary(): KnowledgeSourceSummary {
    let knowledgeHubUsageSummary = this.knowledgeHubService.usageSummary || {};

    return Object.entries(knowledgeHubUsageSummary).reduce((summary, [key, value]) => {
      summary[Number(key)] = {
        entity_type: Number(key),
        total_count: value.all,
        used_by_fin_count: value.agent,
        additional_data: undefined,
      };
      return summary;
    }, {} as KnowledgeSourceSummary);
  }

  @restartableTask
  *loadOperatorIdentity(): TaskGenerator<void> {
    try {
      this.operatorIdentity = yield this.store.findRecord('operator-identity', '1');
      // operator identity can be modified, but not saved, in settings
      // and it looks strange to have a partially edited record when we visit this page
      this.operatorIdentity.rollbackAttributes();
      this.operatorIdentityPreviewImgSrc = this.operatorIdentity.avatar?.image_urls?.square_128;
      this.operatorIdentityPreviewDarkImgSrc =
        this.operatorIdentity.avatarDark?.image_urls?.square_128;
    } catch (e) {
      this.notificationsService.notifyError(this.intl.t('operator.settings.error'));
    }
  }

  get isLoadingOperatorIdentity() {
    return taskFor(this.loadOperatorIdentity).isRunning;
  }

  @action async updateOperatorIdentity() {
    await taskFor(this.saveOperatorIdentity).perform();
  }

  @restartableTask
  *saveOperatorIdentity(): TaskGenerator<void> {
    try {
      yield this.permissionsService.checkPermission('can_create_and_edit_bots');
    } catch (e) {
      return;
    }

    try {
      if (this.app.canUseFeature('bot_identity')) {
        yield this.operatorIdentity.save();
      }

      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.settings.saved-successfully'),
      );
    } catch (error) {
      this.notificationsService.notifyError(this.intl.t('operator.settings.error'));
    }
  }

  get isSavingOperatorIdentity() {
    return taskFor(this.saveOperatorIdentity).isRunning;
  }

  @action
  setOperatorIdentityPreviewImgSrc(url: string) {
    this.operatorIdentityPreviewImgSrc = url;
  }

  @action
  setOperatorIdentityPreviewDarkImgSrc(url: string) {
    this.operatorIdentityPreviewDarkImgSrc = url;
  }

  @action async saveZendeskConfig() {
    if (!this.zendeskConfig || !this.zendeskDataLoader) {
      throw new Error('zendeskConfig or zendeskDataLoader are not present.');
    }

    if (this.zendeskConfig) {
      this.zendeskConfig.subdomain = this.zendeskConfig.subdomain?.trim();
      this.zendeskConfig.username = this.zendeskConfig.username?.trim();
      this.zendeskConfig.token = this.zendeskConfig.token?.trim();

      if (this.zendeskConfig.sunshineIntegration) {
        this.zendeskConfig.sunshineIntegration.sunshineAppId =
          this.zendeskConfig.sunshineIntegration.sunshineAppId?.trim();
        this.zendeskConfig.sunshineIntegration.apiKeyId =
          this.zendeskConfig.sunshineIntegration.apiKeyId?.trim();
        this.zendeskConfig.sunshineIntegration.apiSecret =
          this.zendeskConfig.sunshineIntegration.apiSecret?.trim();
      }
    }

    try {
      await this.zendeskConfig.save();
      taskFor(this.zendeskDataLoader.loadZendeskTicketsSetupData).perform();
      taskFor(this.zendeskDataLoader.loadSunshineChannels).perform();
      taskFor(this.zendeskDataLoader.loadZendeskBrands).perform();
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t('standalone.setup.api.save-error'),
        },
        {
          responseProvidedErrors: RESPONSE_PROVIDED_ERRORS,
        },
      );
      console.error(e.message || e.jqXHR);
      throw e;
    }
  }

  @action async saveFreshdeskConfig() {
    if (!this.freshdeskConfig) {
      throw new Error('freshdeskConfig is not present.');
    }

    if (this.freshdeskConfig) {
      this.freshdeskConfig.subdomain = this.freshdeskConfig.subdomain?.trim();
      this.freshdeskConfig.apiKey = this.freshdeskConfig.apiKey?.trim();
    }

    try {
      await this.freshdeskConfig.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.setup.api.save-success'),
      );
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t('standalone.setup.api.save-error'),
        },
        {
          responseProvidedErrors: RESPONSE_PROVIDED_ERRORS,
        },
      );
      console.error(e.message || e.jqXHR);
      throw e;
    }
  }

  async fetchStandaloneImportSource(): Promise<void> {
    let response = await get('/ember/import_service/import_sources/standalone_sync_import_source', {
      app_id: this.appService.app.id,
    });
    if (response.id) {
      let standaloneImportSource = await this.store.findRecord(
        'import-service/import-source',
        response.id,
        { reload: true },
      );
      if (!standaloneImportSource) {
        this.isConversationSyncingEnabled = true;
      } else {
        this.isConversationSyncingEnabled = standaloneImportSource.isConversationSyncingEnabled;
      }
    } else {
      // Here we have no import source set up.
      // If API is not setup then this is expected - we default to true to push the user to set up syncing.
      // However, if API is setup but we have no import source then we should show false so they can set up syncing.
      let zendeskApp = this.appService.app.hasStandalonePlatform('zendesk');
      let salesforceApp = this.appService.app.hasStandalonePlatform('salesforce');

      if (zendeskApp && this.zendeskConfig?.isPendingSetupForTickets) {
        this.isConversationSyncingEnabled = true;
      } else if (salesforceApp && this.salesforceConfig?.isPendingSetup) {
        this.isConversationSyncingEnabled = true;
      } else {
        this.isConversationSyncingEnabled = false;
      }
    }
  }

  async updateConversationSyncing(): Promise<void> {
    return await put(
      '/ember/import_service/import_sources/update_standalone_conversation_syncing',
      {
        app_id: this.appService.app.id,
        sync_history: this.isConversationSyncingEnabled,
      },
    );
  }

  get zendeskPlatformConvSyncingNotEnabled() {
    return (
      this.appService.app.hasStandalonePlatform('zendesk') &&
      !this.isZendeskConnectedWithSyncingEnabled
    );
  }

  get salesforcePlatformConvSyncingNotEnabled() {
    return (
      this.appService.app.hasStandalonePlatform('salesforce') &&
      !this.isSalesforceConnectedWithSyncingEnabled
    );
  }

  get isLoadingZendeskTicketsSetupData() {
    return this.zendeskDataLoader?.isLoadingZendeskTicketsSetupData || false;
  }

  get finIsLive() {
    return (
      this.zendeskConfig?.isEnabledForTickets ||
      this.zendeskConfig?.sunshineIntegration?.isEnabledForSunshineMessenger ||
      this.salesforceConfig?.isEnabled ||
      this.freshdeskConfig?.isEnabledForTickets ||
      this.intercomMessengerConfig?.isMessengerEnabled ||
      false
    );
  }

  get finIsPaused() {
    return (
      this.zendeskConfig?.isPausedForTickets ||
      this.zendeskConfig?.sunshineIntegration?.isPausedForSunshineMessenger ||
      this.salesforceConfig?.isPaused ||
      this.freshdeskConfig?.isPausedForTickets ||
      this.intercomMessengerConfig?.isMessengerPaused
    );
  }

  get finIsNotLiveAndIsPaused() {
    return !this.finIsLive && this.finIsPaused;
  }

  get isLoadingZendeskConfig() {
    return this.zendeskDataLoader?.isLoadingZendeskConfig || false;
  }

  get isLoadingFreshdeskConfig() {
    return taskFor(this.loadFreshdeskConfig).isRunning;
  }

  get isLoadingFreshdeskTicketsSetupData() {
    return taskFor(this.loadFreshdeskTicketsSetupData).isRunning;
  }

  get isLoadingZendeskSchedules() {
    return this.zendeskDataLoader?.isLoadingZendeskSchedules || false;
  }

  @dropTask *loadAiContentLibrarySummary() {
    yield this.contentImportService.fetchAiContentLibrarySummary();
  }

  get isLoadingAiContentLibrarySummary() {
    return taskFor(this.loadAiContentLibrarySummary).isRunning;
  }

  get isLoadingZendeskDataFields() {
    return this.zendeskDataLoader?.isLoadingZendeskDataFields || false;
  }

  @dropTask *peekOrLoadMessengerSettings(): TaskGenerator<void> {
    this.messengerSettings =
      this.store.peekRecord('messenger-settings/all', this.app.id) ||
      (yield this.store.findRecord('messenger-settings/all', this.app.id));
  }

  get standaloneConversationField(): (id: string | undefined) => TicketField | undefined {
    return (id: string) => {
      return (
        this.zendeskDataFields.ticketFields.find((field) => field.intercom_cvda?.id === id) ||
        this.salesforceDataFields.caseFields.find((field) => field.intercom_cvda?.id === id)
      );
    };
  }

  isMappedUserAttribute(attributeIdentifier: string) {
    let userFields = [] as UserField[];

    if (this.app.hasStandalonePlatform('zendesk')) {
      userFields = this.zendeskDataFields.userFields;
    } else if (this.app.hasStandalonePlatform('salesforce')) {
      userFields = this.salesforceDataFields.userFields;
    }

    return userFields.any(
      (userField) =>
        userField.intercom_cda?.identifier === attributeIdentifier ||
        userField.intercom_sda?.identifier === attributeIdentifier,
    );
  }

  isMappedCompanyAttribute(attributeIdentifier: string) {
    if (this.app.hasStandalonePlatform('zendesk')) {
      let organizationFields = this.zendeskDataFields.organizationFields;

      return organizationFields.any(
        (organizationField) =>
          `company.${organizationField.intercom_cda?.identifier}` === attributeIdentifier ||
          `company.${organizationField.intercom_sda?.identifier}` === attributeIdentifier,
      );
    } else if (this.app.hasStandalonePlatform('salesforce')) {
      let externalFieldIdentifiers: EmberArray<ExternalFieldIdentifier> = this.store.peekAll(
        'standalone/external-field-identifier',
      );

      return externalFieldIdentifiers.any(
        (externalFieldIdentifier) =>
          externalFieldIdentifier.intercomAttributeIdentifier === attributeIdentifier,
      );
    } else {
      return false;
    }
  }

  isMappedConversationAttribute(attributeIdentifier: string) {
    let ticketFields = [] as TicketField[];

    if (this.app.hasStandalonePlatform('zendesk')) {
      ticketFields = this.zendeskDataFields.ticketFields;
    } else if (this.app.hasStandalonePlatform('salesforce')) {
      ticketFields = this.salesforceDataFields.caseFields;
    }

    return ticketFields.any(
      (ticketField) => `conversation.${ticketField.intercom_cvda?.id}` === attributeIdentifier,
    );
  }

  platformIcon(): InterfaceIconName | undefined {
    if (this.appService.app.hasStandalonePlatform('zendesk')) {
      return StandalonePlatformToIcon['zendesk'];
    } else if (this.appService.app.hasStandalonePlatform('salesforce')) {
      return StandalonePlatformToIcon['salesforce'];
    }

    return undefined;
  }
}
